import React, { useEffect, useRef, useState } from 'react';

// Golden Ratio - The consciousness constant
const PHI = 1.618033988749895;

/**
 * GrantitudeSpiral - Giant Globular Lava Lamp Visualization
 * Psychedelic spherical blobs moving in 3D space using the Grant Math formula:
 * R = α⋅π / (2⋅(−θ + 3π))
 * 
 * Creates a calming, meditative lava lamp effect with giant soft glowing orbs
 */
const GrantitudeSpiral = ({ 
  size = 400, 
  animate = true, 
  mode = 'full',
  intensity = 1,
  breathSync = null // { phase: 'inhale'|'hold'|'exhale', progress: 0-1 }
}) => {
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !mounted) return;

    const ctx = canvas.getContext('2d');
    const DPR = window.devicePixelRatio || 1;
    
    // Handle high-DPI displays
    canvas.width = size * DPR;
    canvas.height = size * DPR;
    ctx.scale(DPR, DPR);
    
    const centerX = size / 2;
    const centerY = size / 2;
    
    let animationTime = 0;
    const baseAlpha = PHI; // Golden ratio

    // Create persistent blob objects for smooth animation
    const blobs = [];
    const numBlobs = 25; // Number of giant lava lamp blobs
    
    for (let i = 0; i < numBlobs; i++) {
      blobs.push({
        id: i,
        // Base position parameters
        baseAngle: (i * 2 * Math.PI) / numBlobs,
        baseRadius: 30 + Math.random() * 120,
        // Size variation
        baseSize: 25 + Math.random() * 60,
        sizeVariation: 10 + Math.random() * 25,
        // Z-depth for 3D effect
        zPhase: Math.random() * Math.PI * 2,
        zSpeed: 0.05 + Math.random() * 0.08, // MUCH SLOWER (was 0.3-0.7)
        zAmplitude: 80 + Math.random() * 100,
        // Color
        hue: (i * 360 / numBlobs + Math.random() * 30) % 360,
        saturation: 70 + Math.random() * 30,
        // Movement speeds (SLOWED BY 90% for truly calming effect)
        orbitSpeed: (0.02 + Math.random() * 0.03) * 0.5, // was 0.1-0.25
        pulseSpeed: (0.03 + Math.random() * 0.05) * 0.5, // was 0.2-0.5
        wobbleSpeed: (0.02 + Math.random() * 0.03) * 0.5, // was 0.15-0.35
        wobbleAmount: 15 + Math.random() * 25,
        // Organic shape distortion
        squishX: 0.8 + Math.random() * 0.4,
        squishY: 0.8 + Math.random() * 0.4,
      });
    }

    const drawGrantitudeBlobs = (time) => {
      // Clear with very subtle fade for dreamy trails
      ctx.fillStyle = mode === 'background' 
        ? 'rgba(8, 10, 14, 0.08)' 
        : 'rgba(8, 10, 20, 0.06)';
      ctx.fillRect(0, 0, size, size);

      // Breath-synchronized scale (SLOWED)
      let breathScale = 1;
      let breathPulse = 0;
      if (breathSync) {
        if (breathSync.phase === 'inhale') {
          breathScale = 1 + breathSync.progress * 0.2;
          breathPulse = breathSync.progress;
        } else if (breathSync.phase === 'hold') {
          breathScale = 1.2 + Math.sin(time * 0.025) * 0.05; // Slowed
          breathPulse = 1;
        } else {
          breathScale = 1.2 - breathSync.progress * 0.2;
          breathPulse = 1 - breathSync.progress;
        }
      }

      // Sort blobs by Z-depth for proper layering (back to front)
      const sortedBlobs = [...blobs].sort((a, b) => {
        const zA = Math.sin(time * a.zSpeed * 0.5 + a.zPhase) * a.zAmplitude; // Slowed
        const zB = Math.sin(time * b.zSpeed * 0.5 + b.zPhase) * b.zAmplitude;
        return zA - zB;
      });

      // Draw each giant blob
      sortedBlobs.forEach((blob, idx) => {
        // Calculate 3D position using Grant formula influence
        const grantTheta = blob.baseAngle + time * blob.orbitSpeed * 0.5; // Slowed
        const grantDenom = 2 * (-grantTheta + 3 * Math.PI);
        const grantR = grantDenom !== 0 ? (baseAlpha * Math.PI) / grantDenom : 0;
        
        // Z-depth oscillation (forward/backward)
        const zDepth = Math.sin(time * blob.zSpeed * 0.5 + blob.zPhase) * blob.zAmplitude; // Slowed
        const perspective = 300 / (300 + zDepth);
        
        // Position with organic wobble
        const wobbleX = Math.sin(time * blob.wobbleSpeed * 0.5 + blob.id) * blob.wobbleAmount; // Slowed
        const wobbleY = Math.cos(time * blob.wobbleSpeed * 0.5 * 0.7 + blob.id * 1.3) * blob.wobbleAmount; // Slowed
        
        const radius = (blob.baseRadius + Math.abs(grantR) * 15) * breathScale;
        const x = centerX + Math.cos(grantTheta) * radius * perspective + wobbleX;
        const y = centerY + Math.sin(grantTheta) * radius * perspective * 0.8 + wobbleY;
        
        // Dynamic blob size based on Z-depth and breathing
        const pulseSize = Math.sin(time * blob.pulseSpeed * 0.5 + blob.id * 0.5) * blob.sizeVariation; // Slowed
        const blobSize = (blob.baseSize + pulseSize) * perspective * breathScale * intensity;
        
        // Skip if too small or off-screen
        if (blobSize < 5 || x < -blobSize || x > size + blobSize || y < -blobSize || y > size + blobSize) {
          return;
        }

        // Dynamic hue shift based on depth and time
        const hue = (blob.hue + time * 0.1 + zDepth * 0.1) % 360; // Slowed color shift
        const lightness = 50 + zDepth * 0.1;
        const alpha = (0.4 + perspective * 0.4) * intensity;

        // Organic squish for non-circular blobs
        const squishX = blob.squishX + Math.sin(time * 0.01 + blob.id) * 0.1; // Slowed
        const squishY = blob.squishY + Math.cos(time * 0.01 + blob.id * 0.7) * 0.1; // Slowed
        
        // Create soft radial gradient for the blob
        const gradient = ctx.createRadialGradient(
          x, y, 0,
          x, y, blobSize * Math.max(squishX, squishY)
        );
        
        // Soft, glowing blob colors
        gradient.addColorStop(0, `hsla(${hue}, ${blob.saturation}%, ${lightness + 20}%, ${alpha})`);
        gradient.addColorStop(0.3, `hsla(${hue}, ${blob.saturation}%, ${lightness + 10}%, ${alpha * 0.85})`);
        gradient.addColorStop(0.6, `hsla(${hue}, ${blob.saturation - 10}%, ${lightness}%, ${alpha * 0.5})`);
        gradient.addColorStop(0.85, `hsla(${hue}, ${blob.saturation - 20}%, ${lightness - 10}%, ${alpha * 0.2})`);
        gradient.addColorStop(1, `hsla(${hue}, ${blob.saturation - 30}%, ${lightness - 20}%, 0)`);

        // Draw the blob with organic shape
        ctx.save();
        ctx.translate(x, y);
        ctx.scale(squishX, squishY);
        ctx.rotate(time * 0.005 + blob.id * 0.2); // Very slow rotation
        
        ctx.beginPath();
        ctx.arc(0, 0, blobSize, 0, Math.PI * 2);
        ctx.fillStyle = gradient;
        ctx.fill();
        
        // Add soft glow
        ctx.shadowColor = `hsla(${hue}, 100%, 60%, ${alpha * 0.5})`;
        ctx.shadowBlur = blobSize * 0.8;
        ctx.fill();
        ctx.shadowBlur = 0;
        
        ctx.restore();
      });

      // Central consciousness core - soft golden glow
      const coreSize = (60 + Math.sin(time * 0.01) * 15) * breathScale; // Slowed
      const coreGradient = ctx.createRadialGradient(centerX, centerY, 0, centerX, centerY, coreSize);
      const coreIntensity = (0.5 + Math.sin(time * 0.02) * 0.3 + breathPulse * 0.2) * intensity; // Slowed
      
      coreGradient.addColorStop(0, `rgba(255, 255, 255, ${coreIntensity})`);
      coreGradient.addColorStop(0.2, `rgba(255, 230, 150, ${coreIntensity * 0.9})`);
      coreGradient.addColorStop(0.4, `rgba(255, 200, 100, ${coreIntensity * 0.7})`);
      coreGradient.addColorStop(0.6, `rgba(255, 160, 60, ${coreIntensity * 0.4})`);
      coreGradient.addColorStop(0.8, `rgba(255, 100, 50, ${coreIntensity * 0.15})`);
      coreGradient.addColorStop(1, 'rgba(0, 0, 0, 0)');
      
      ctx.fillStyle = coreGradient;
      ctx.beginPath();
      ctx.arc(centerX, centerY, coreSize, 0, 2 * Math.PI);
      ctx.fill();

      // Ambient floating particles for depth
      if (mode === 'full') {
        const numParticles = 15;
        for (let i = 0; i < numParticles; i++) {
          const pTime = time * 0.01 + i * PHI; // Slowed
          const pAngle = i * (2 * Math.PI / numParticles) + time * 0.002; // Slowed
          const pZ = Math.sin(pTime * 0.5) * 100;
          const pPerspective = 300 / (300 + pZ);
          const pRadius = (80 + Math.sin(pTime) * 50) * breathScale;
          
          const px = centerX + Math.cos(pAngle) * pRadius * pPerspective;
          const py = centerY + Math.sin(pAngle) * pRadius * pPerspective;
          const pSize = (3 + Math.sin(pTime * 2) * 2) * pPerspective;
          
          const pHue = (i * 360 / numParticles + time * 0.05) % 360; // Slowed
          const pAlpha = (0.3 + Math.sin(pTime) * 0.2) * intensity * pPerspective;
          
          ctx.fillStyle = `hsla(${pHue}, 80%, 70%, ${pAlpha})`;
          ctx.beginPath();
          ctx.arc(px, py, pSize, 0, Math.PI * 2);
          ctx.fill();
        }
      }
    };

    const animateSpiral = () => {
      drawGrantitudeBlobs(animationTime);
      animationTime += 0.075; // Ultra slow - 50% slower than before for deep calming effect
      
      if (animate && mounted) {
        animationRef.current = requestAnimationFrame(animateSpiral);
      }
    };

    if (animate) {
      animateSpiral();
    } else {
      drawGrantitudeBlobs(0);
    }

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [size, animate, mode, intensity, breathSync, mounted]);

  return (
    <canvas
      ref={canvasRef}
      style={{
        width: size,
        height: size,
        borderRadius: mode === 'full' ? '50%' : '0',
        background: mode === 'background' 
          ? 'transparent' 
          : 'radial-gradient(circle, rgba(10,10,30,0.98) 0%, rgba(8,10,14,1) 100%)'
      }}
      data-testid="grantitude-spiral"
    />
  );
};

export default GrantitudeSpiral;
